import { useState, useMemo, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  FileText,
  CheckCircle,
  Edit2,
  Trash2,
  AlertCircle,
  Search,
  Filter,
  MoreVertical,
  Banknote,
  Percent,
  X,
  Save,
  ArrowUpDown,
  Settings2,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { ColumnConfiguratorDrawer } from "@/components/column-configurator-drawer";
import type { ColumnDefinition } from "@/components/column-configurator-drawer";

interface WipItem {
  id: string;
  date: string;
  resource: string;
  task: string;
  description: string;
  type: 'Time' | 'Expense' | 'Purchase';
  quantity: number;
  unit: string;
  rate: number; // Bill Rate
  amount: number;
  costCentre?: string;
  reference?: string;
  costRate?: number;
}

interface InvoiceLineItem {
  id: string;
  date: Date;
  type: 'Time' | 'Expense' | 'Purchase';
  resource: string;
  description: string;
  costCentre: string;
  reference: string;
  originalValue: number; // Original amount before any edits
  quantity: number;
  unit: string;
  rate: number;
  costRate: number;
  amount: number;
  originalWipId?: string;
}

interface InvoiceGenerationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedWipItems: WipItem[];
  projectName: string;
  onInvoiceGenerated?: (invoice: any) => void;
}

export function InvoiceGenerationWizard({
  open,
  onOpenChange,
  selectedWipItems,
  projectName,
  onInvoiceGenerated,
}: InvoiceGenerationWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [invoiceLineItems, setInvoiceLineItems] = useState<InvoiceLineItem[]>([]);
  
  // Step 1: Inline editing state (combined review & edit)
  const [editingCellId, setEditingCellId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [lineSearchQuery, setLineSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<"description" | "quantity" | "rate" | "amount">("description");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  
  // Pagination state for Step 1
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  
  // Column configuration state for Step 1
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnDefinition[]>([
    { id: "date", name: "Date", label: "Date", description: "Transaction date", visible: true, locked: false, category: "Timeline", order: 0 },
    { id: "type", name: "Type", label: "Type", description: "Transaction type", visible: true, locked: false, category: "Status", order: 1 },
    { id: "resource", name: "Resource", label: "Resource", description: "Resource name", visible: true, locked: false, category: "Resource", order: 2 },
    { id: "description", name: "Description", label: "Description", description: "Line item description", visible: true, locked: true, category: "Status", order: 3 },
    { id: "costCentre", name: "Cost Centre", label: "Cost Centre", description: "Cost centre code", visible: false, locked: false, category: "Financial", order: 4 },
    { id: "reference", name: "Reference", label: "Reference", description: "Reference number", visible: false, locked: false, category: "Status", order: 5 },
    { id: "originalValue", name: "Original Value", label: "Original Value", description: "Original WIP value", visible: true, locked: false, category: "Financial", order: 6, align: "right" },
    { id: "quantity", name: "Qty", label: "Qty", description: "Quantity", visible: true, locked: false, category: "Financial", order: 7, align: "right" },
    { id: "rate", name: "Rate", label: "Rate", description: "Bill rate", visible: true, locked: false, category: "Financial", order: 8, align: "right" },
    { id: "amount", name: "Amount", label: "Amount", description: "Line total", visible: true, locked: false, category: "Financial", order: 9, align: "right" },
  ]);

  // Step 2: Invoice details
  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date());
  const [dueDate, setDueDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30); // Default 30 days
    return date;
  });
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [invoiceReference, setInvoiceReference] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("30");

  // Initialize line items from WIP items when wizard opens
  useEffect(() => {
    if (open && selectedWipItems.length > 0) {
      const lineItems: InvoiceLineItem[] = selectedWipItems.map((item) => ({
        id: `line-${item.id}`,
        date: new Date(item.date),
        type: item.type,
        resource: item.resource,
        description: `${item.task} - ${item.description}`,
        costCentre: item.costCentre || '—',
        reference: item.reference || '—',
        originalValue: item.amount,
        quantity: item.quantity,
        unit: item.unit,
        rate: item.rate,
        costRate: item.costRate || 0,
        amount: item.amount,
        originalWipId: item.id,
      }));
      setInvoiceLineItems(lineItems);
      setSelectedLineIds([]);
      setLineSearchQuery("");
      setCurrentStep(1);
    }
  }, [open, selectedWipItems]);

  // Filter and sort line items
  const filteredAndSortedLines = useMemo(() => {
    let filtered = invoiceLineItems.filter((item) =>
      lineSearchQuery === "" ||
      item.description.toLowerCase().includes(lineSearchQuery.toLowerCase()) ||
      item.resource.toLowerCase().includes(lineSearchQuery.toLowerCase()) ||
      item.costCentre.toLowerCase().includes(lineSearchQuery.toLowerCase()) ||
      item.reference.toLowerCase().includes(lineSearchQuery.toLowerCase())
    );

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc" 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortDirection === "asc" 
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return filtered;
  }, [invoiceLineItems, lineSearchQuery, sortColumn, sortDirection]);

  // Visible columns
  const visibleColumns = useMemo(() => columns.filter((col) => col.visible), [columns]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedLines.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLines = filteredAndSortedLines.slice(startIndex, endIndex);
  const startItem = filteredAndSortedLines.length > 0 ? startIndex + 1 : 0;
  const endItem = Math.min(endIndex, filteredAndSortedLines.length);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [lineSearchQuery, sortColumn, sortDirection]);

  // Calculate totals
  const totals = useMemo(() => {
    const subtotal = invoiceLineItems.reduce((sum, item) => sum + item.amount, 0);
    const tax = 0; // Can be configured later
    const total = subtotal + tax;

    return { subtotal, tax, total };
  }, [invoiceLineItems]);

  // Handle step navigation
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setInvoiceLineItems([]);
    setEditingCellId(null);
    setEditingValue("");
    setSelectedLineIds([]);
    setLineSearchQuery("");
    setInvoiceNotes("");
    setInvoiceReference("");
    onOpenChange(false);
  };

  // Inline editing handlers
  const handleCellClick = (lineId: string, field: "description" | "quantity" | "rate", currentValue: string | number) => {
    setEditingCellId(`${lineId}-${field}`);
    // For numbers, preserve decimal places
    if (field === "quantity" || field === "rate") {
      setEditingValue(typeof currentValue === "number" ? currentValue.toFixed(2) : String(currentValue));
    } else {
      setEditingValue(String(currentValue));
    }
  };

  const handleCellSave = (lineId: string, field: "description" | "quantity" | "rate") => {
    setInvoiceLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineId) return item;

        if (field === "description") {
          return { ...item, description: editingValue };
        } else if (field === "quantity") {
          const newQuantity = parseFloat(editingValue) || 0;
          return { ...item, quantity: newQuantity, amount: newQuantity * item.rate };
        } else if (field === "rate") {
          const newRate = parseFloat(editingValue) || 0;
          return { ...item, rate: newRate, amount: item.quantity * newRate };
        }
        return item;
      })
    );
    setEditingCellId(null);
    setEditingValue("");
  };

  const handleCellCancel = () => {
    setEditingCellId(null);
    setEditingValue("");
  };

  const handleCellKeyDown = (e: React.KeyboardEvent, lineId: string, field: "description" | "quantity" | "rate") => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCellSave(lineId, field);
      
      // Move to next row, same column
      const currentIndex = paginatedLines.findIndex((line) => line.id === lineId);
      if (currentIndex < paginatedLines.length - 1) {
        const nextLine = paginatedLines[currentIndex + 1];
        setTimeout(() => {
          handleCellClick(nextLine.id, field, field === "description" ? nextLine.description : field === "quantity" ? nextLine.quantity : nextLine.rate);
        }, 0);
      }
    } else if (e.key === "Escape") {
      handleCellCancel();
    } else if (e.key === "Tab") {
      e.preventDefault();
      handleCellSave(lineId, field);
      
      // Define the tab order for editable fields
      const editableFields: ("description" | "quantity" | "rate")[] = ["description", "quantity", "rate"];
      const currentFieldIndex = editableFields.indexOf(field);
      
      if (e.shiftKey) {
        // Shift+Tab: Move to previous field
        if (currentFieldIndex > 0) {
          // Previous field in same row
          const prevField = editableFields[currentFieldIndex - 1];
          const currentLine = paginatedLines.find((line) => line.id === lineId);
          if (currentLine) {
            setTimeout(() => {
              handleCellClick(lineId, prevField, prevField === "description" ? currentLine.description : prevField === "quantity" ? currentLine.quantity : currentLine.rate);
            }, 0);
          }
        } else {
          // First field in row, go to last field of previous row
          const currentIndex = paginatedLines.findIndex((line) => line.id === lineId);
          if (currentIndex > 0) {
            const prevLine = paginatedLines[currentIndex - 1];
            const lastField = editableFields[editableFields.length - 1];
            setTimeout(() => {
              handleCellClick(prevLine.id, lastField, lastField === "rate" ? prevLine.rate : prevLine.quantity);
            }, 0);
          }
        }
      } else {
        // Tab: Move to next field
        if (currentFieldIndex < editableFields.length - 1) {
          // Next field in same row
          const nextField = editableFields[currentFieldIndex + 1];
          const currentLine = paginatedLines.find((line) => line.id === lineId);
          if (currentLine) {
            setTimeout(() => {
              handleCellClick(lineId, nextField, nextField === "description" ? currentLine.description : nextField === "quantity" ? currentLine.quantity : currentLine.rate);
            }, 0);
          }
        } else {
          // Last field in row, go to first field of next row
          const currentIndex = paginatedLines.findIndex((line) => line.id === lineId);
          if (currentIndex < paginatedLines.length - 1) {
            const nextLine = paginatedLines[currentIndex + 1];
            const firstField = editableFields[0];
            setTimeout(() => {
              handleCellClick(nextLine.id, firstField, nextLine.description);
            }, 0);
          }
        }
      }
    }
  };

  // Selection handlers
  const handleSelectLine = (lineId: string) => {
    setSelectedLineIds((prev) =>
      prev.includes(lineId) ? prev.filter((id) => id !== lineId) : [...prev, lineId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLineIds.length === filteredAndSortedLines.length) {
      setSelectedLineIds([]);
    } else {
      setSelectedLineIds(filteredAndSortedLines.map((line) => line.id));
    }
  };

  // Bulk actions
  const handleDeleteSelected = () => {
    setInvoiceLineItems((prev) => prev.filter((item) => !selectedLineIds.includes(item.id)));
    setSelectedLineIds([]);
  };

  const handleBulkRateChange = (changeType: "increase" | "decrease", value: number, isPercent: boolean) => {
    setInvoiceLineItems((prev) =>
      prev.map((item) => {
        if (!selectedLineIds.includes(item.id)) return item;

        let newRate = item.rate;
        if (isPercent) {
          newRate = changeType === "increase" 
            ? item.rate * (1 + value / 100)
            : item.rate * (1 - value / 100);
        } else {
          newRate = changeType === "increase" 
            ? item.rate + value
            : item.rate - value;
        }

        return {
          ...item,
          rate: Math.max(0, newRate),
          amount: item.quantity * Math.max(0, newRate),
        };
      })
    );
  };

  const handleDeleteLine = (lineId: string) => {
    setInvoiceLineItems((prev) => prev.filter((item) => item.id !== lineId));
  };

  // Handle invoice generation
  const handleGenerateInvoice = () => {
    const invoice = {
      date: invoiceDate,
      dueDate,
      reference: invoiceReference,
      notes: invoiceNotes,
      paymentTerms,
      lineItems: invoiceLineItems,
      totals,
      projectName,
    };

    console.log("Generated Invoice:", invoice);
    onInvoiceGenerated?.(invoice);
    handleClose();
  };

  // Handle save as draft
  const handleSaveAsDraft = () => {
    const draftInvoice = {
      status: "Draft",
      date: invoiceDate,
      dueDate,
      reference: invoiceReference,
      notes: invoiceNotes,
      paymentTerms,
      lineItems: invoiceLineItems,
      totals,
      projectName,
      currentStep,
    };

    console.log("Saving invoice as draft:", draftInvoice);
    // Here you would save the invoice with status "Draft"
    alert("Invoice saved as draft.");
    
    // Close the wizard after saving
    onOpenChange(false);
  };

  // Update payment terms when changed
  const handlePaymentTermsChange = (value: string) => {
    setPaymentTerms(value);
    if (value !== "custom") {
      const days = parseInt(value, 10);
      const newDueDate = new Date(invoiceDate);
      newDueDate.setDate(newDueDate.getDate() + days);
      setDueDate(newDueDate);
    }
  };

  const handleSort = (column: typeof sortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  // Helper function to render cell content based on column ID
  const renderCell = (line: InvoiceLineItem, columnId: string) => {
    switch (columnId) {
      case "date":
        return <TableCell className="text-sm">{format(line.date, "dd/MM/yyyy")}</TableCell>;
      
      case "type":
        return (
          <TableCell>
            <Badge variant={line.type === 'Time' ? 'default' : line.type === 'Expense' ? 'secondary' : 'outline'}>
              {line.type}
            </Badge>
          </TableCell>
        );
      
      case "resource":
        return <TableCell className="text-sm">{line.resource}</TableCell>;
      
      case "description":
        return (
          <TableCell
            className="cursor-text hover:bg-muted/50 transition-colors"
            onClick={() => editingCellId !== `${line.id}-description` && handleCellClick(line.id, "description", line.description)}
          >
            {editingCellId === `${line.id}-description` ? (
              <Textarea
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleCellSave(line.id, "description")}
                onKeyDown={(e) => handleCellKeyDown(e, line.id, "description")}
                className="min-h-[60px] w-full"
                autoFocus
              />
            ) : (
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm">{line.description}</span>
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              </div>
            )}
          </TableCell>
        );
      
      case "costCentre":
        return <TableCell className="text-sm">{line.costCentre}</TableCell>;
      
      case "reference":
        return <TableCell className="text-sm">{line.reference}</TableCell>;
      
      case "originalValue":
        return (
          <TableCell className="text-right text-sm text-muted-foreground">
            {formatCurrency(line.originalValue)}
          </TableCell>
        );
      
      case "quantity":
        return (
          <TableCell
            className="text-right cursor-text hover:bg-muted/50 transition-colors"
            onClick={() => editingCellId !== `${line.id}-quantity` && handleCellClick(line.id, "quantity", line.quantity)}
          >
            {editingCellId === `${line.id}-quantity` ? (
              <Input
                type="number"
                step="0.01"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleCellSave(line.id, "quantity")}
                onKeyDown={(e) => handleCellKeyDown(e, line.id, "quantity")}
                onFocus={(e) => e.target.select()}
                className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                autoFocus
              />
            ) : (
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm">{line.quantity.toFixed(2)}</span>
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              </div>
            )}
          </TableCell>
        );
      
      case "rate":
        return (
          <TableCell
            className="text-right cursor-text hover:bg-muted/50 transition-colors"
            onClick={() => editingCellId !== `${line.id}-rate` && handleCellClick(line.id, "rate", line.rate)}
          >
            {editingCellId === `${line.id}-rate` ? (
              <Input
                type="number"
                step="0.01"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={() => handleCellSave(line.id, "rate")}
                onKeyDown={(e) => handleCellKeyDown(e, line.id, "rate")}
                onFocus={(e) => e.target.select()}
                className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                autoFocus
              />
            ) : (
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm">{formatCurrency(line.rate)}</span>
                <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
              </div>
            )}
          </TableCell>
        );
      
      case "amount":
        return (
          <TableCell className="text-right text-sm font-medium">
            {formatCurrency(line.amount)}
          </TableCell>
        );
      
      default:
        return <TableCell></TableCell>;
    }
  };

  if (!open) return null;

  return (
    <>
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Header Bar */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Generate Invoice</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Create a new invoice from selected unbilled work items • {projectName}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between max-w-2xl">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors",
                    currentStep >= step
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-muted-foreground text-muted-foreground"
                  )}
                >
                  {currentStep > step ? <CheckCircle className="h-5 w-5" /> : step}
                </div>
                <div className="flex-1 px-3">
                  <div
                    className={cn(
                      "text-sm font-medium",
                      currentStep >= step ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {step === 1 && "Review & Edit Lines"}
                    {step === 2 && "Invoice Details"}
                    {step === 3 && "Confirm"}
                  </div>
                </div>
                {step < 3 && (
                  <div
                    className={cn(
                      "h-0.5 flex-1",
                      currentStep > step ? "bg-primary" : "bg-muted"
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-6 py-6">
            {/* Step 1: Review & Edit Invoice Line Items - COMBINED */}
            {currentStep === 1 && (
              <div className="space-y-4 flex flex-col">
                {/* Header with totals and bulk actions */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-lg">Invoice Line Items</h3>
                    <p className="text-sm text-muted-foreground">
                      {invoiceLineItems.length} lines • Click any cell to edit
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Subtotal</div>
                    <div className="text-2xl font-bold">{formatCurrency(totals.subtotal)}</div>
                  </div>
                </div>

                {/* Search and Bulk Actions Toolbar */}
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-background">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search line items..."
                      value={lineSearchQuery}
                      onChange={(e) => setLineSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                    {lineSearchQuery && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setLineSearchQuery("")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {selectedLineIds.length > 0 && (
                    <>
                      <Badge variant="secondary" className="px-3 py-1">
                        {selectedLineIds.length} selected
                      </Badge>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Banknote className="h-4 w-4 mr-2" />
                            Bulk Rate Change
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel>Adjust Rates</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 10, true)}>
                            <Percent className="h-4 w-4 mr-2" />
                            Increase by 10%
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 20, true)}>
                            <Percent className="h-4 w-4 mr-2" />
                            Increase by 20%
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBulkRateChange("decrease", 10, true)}>
                            <Percent className="h-4 w-4 mr-2" />
                            Decrease by 10%
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 10, false)}>
                            <Banknote className="h-4 w-4 mr-2" />
                            Increase by £10
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBulkRateChange("increase", 25, false)}>
                            <Banknote className="h-4 w-4 mr-2" />
                            Increase by £25
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteSelected}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove Selected
                      </Button>
                    </>
                  )}

                  {/* Configure Columns Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setColumnConfigOpen(true)}
                  >
                    <Settings2 className="h-4 w-4 mr-2" />
                    Configure
                  </Button>
                </div>

                {/* Editable Table - Full Height */}
                <div className="border rounded-lg flex flex-col overflow-hidden max-h-[calc(100vh-32rem)]">
                  <div className="flex-1 overflow-auto relative">
                    <Table disableScroll>
                      <TableHeader className="sticky top-0 z-20 bg-background shadow-sm after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-border">
                        <TableRow className="bg-background">
                          <TableHead className="w-12 bg-background sticky top-0">
                            <Checkbox
                              checked={selectedLineIds.length === filteredAndSortedLines.length && filteredAndSortedLines.length > 0}
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          {visibleColumns.map((column) => (
                            <TableHead 
                              key={column.id}
                              className={cn(
                                "bg-background sticky top-0 cursor-pointer hover:bg-muted/50",
                                column.align === "right" && "text-right"
                              )}
                              onClick={() => handleSort(column.id as any)}
                            >
                              <div className={cn(
                                "flex items-center gap-2",
                                column.align === "right" && "justify-end"
                              )}>
                                {column.label}
                                <ArrowUpDown className="h-4 w-4" />
                              </div>
                            </TableHead>
                          ))}
                          <TableHead className="w-12 bg-background sticky top-0"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedLines.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={visibleColumns.length + 2} className="text-center py-12 text-muted-foreground">
                              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                              <p>No line items found</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedLines.map((line) => (
                            <TableRow
                              key={line.id}
                              className={cn(
                                "group",
                                selectedLineIds.includes(line.id) && "bg-muted/50"
                              )}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={selectedLineIds.includes(line.id)}
                                  onCheckedChange={() => handleSelectLine(line.id)}
                                />
                              </TableCell>
                              
                              {/* Dynamically render only visible columns */}
                              {visibleColumns.map((column) => {
                                switch (column.id) {
                                  case "date":
                                    return (
                                      <TableCell key={column.id} className="text-sm">
                                        {format(line.date, "dd/MM/yyyy")}
                                      </TableCell>
                                    );
                                  
                                  case "type":
                                    return (
                                      <TableCell key={column.id}>
                                        <Badge variant={line.type === 'Time' ? 'default' : line.type === 'Expense' ? 'secondary' : 'outline'}>
                                          {line.type}
                                        </Badge>
                                      </TableCell>
                                    );
                                  
                                  case "resource":
                                    return (
                                      <TableCell key={column.id} className="text-sm">
                                        {line.resource}
                                      </TableCell>
                                    );
                                  
                                  case "description":
                                    return (
                                      <TableCell
                                        key={column.id}
                                        className="cursor-text hover:bg-muted/50 transition-colors"
                                        onClick={() => editingCellId !== `${line.id}-description` && handleCellClick(line.id, "description", line.description)}
                                      >
                                        {editingCellId === `${line.id}-description` ? (
                                          <Textarea
                                            value={editingValue}
                                            onChange={(e) => setEditingValue(e.target.value)}
                                            onBlur={() => handleCellSave(line.id, "description")}
                                            onKeyDown={(e) => handleCellKeyDown(e, line.id, "description")}
                                            className="min-h-[60px] w-full"
                                            autoFocus
                                          />
                                        ) : (
                                          <div className="flex items-center gap-2">
                                            <span className="flex-1 text-sm">{line.description}</span>
                                            <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                          </div>
                                        )}
                                      </TableCell>
                                    );
                                  
                                  case "costCentre":
                                    return (
                                      <TableCell key={column.id} className="text-sm">
                                        {line.costCentre}
                                      </TableCell>
                                    );
                                  
                                  case "reference":
                                    return (
                                      <TableCell key={column.id} className="text-sm">
                                        {line.reference}
                                      </TableCell>
                                    );
                                  
                                  case "originalValue":
                                    return (
                                      <TableCell key={column.id} className="text-right text-sm text-muted-foreground">
                                        {formatCurrency(line.originalValue)}
                                      </TableCell>
                                    );
                                  
                                  case "quantity":
                                    return (
                                      <TableCell
                                        key={column.id}
                                        className="text-right cursor-text hover:bg-muted/50 transition-colors"
                                        onClick={() => editingCellId !== `${line.id}-quantity` && handleCellClick(line.id, "quantity", line.quantity)}
                                      >
                                        {editingCellId === `${line.id}-quantity` ? (
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={editingValue}
                                            onChange={(e) => setEditingValue(e.target.value)}
                                            onBlur={() => handleCellSave(line.id, "quantity")}
                                            onKeyDown={(e) => handleCellKeyDown(e, line.id, "quantity")}
                                            onFocus={(e) => e.target.select()}
                                            className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            autoFocus
                                          />
                                        ) : (
                                          <div className="flex items-center justify-end gap-2">
                                            <span className="text-sm">
                                              {line.quantity.toFixed(2)}
                                            </span>
                                            <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                          </div>
                                        )}
                                      </TableCell>
                                    );
                                  
                                  case "rate":
                                    return (
                                      <TableCell
                                        key={column.id}
                                        className="text-right cursor-text hover:bg-muted/50 transition-colors"
                                        onClick={() => editingCellId !== `${line.id}-rate` && handleCellClick(line.id, "rate", line.rate)}
                                      >
                                        {editingCellId === `${line.id}-rate` ? (
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={editingValue}
                                            onChange={(e) => setEditingValue(e.target.value)}
                                            onBlur={() => handleCellSave(line.id, "rate")}
                                            onKeyDown={(e) => handleCellKeyDown(e, line.id, "rate")}
                                            onFocus={(e) => e.target.select()}
                                            className="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            autoFocus
                                          />
                                        ) : (
                                          <div className="flex items-center justify-end gap-2">
                                            <span className="text-sm">{formatCurrency(line.rate)}</span>
                                            <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                                          </div>
                                        )}
                                      </TableCell>
                                    );
                                  
                                  case "amount":
                                    return (
                                      <TableCell key={column.id} className="text-right font-medium text-sm">
                                        {formatCurrency(line.amount)}
                                      </TableCell>
                                    );
                                  
                                  default:
                                    return <TableCell key={column.id}></TableCell>;
                                }
                              })}

                              {/* Actions */}
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleDeleteLine(line.id)}>
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Remove Line
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Pagination */}
                {filteredAndSortedLines.length > itemsPerPage && (
                  <div className="flex items-center justify-between px-2">
                    <div className="text-sm text-muted-foreground">
                      Showing {startItem}-{endItem} of {filteredAndSortedLines.length} line items
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground mr-2">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}



            {/* Step 2: Invoice Details */}
            {currentStep === 2 && (
              <div className="space-y-6 max-w-4xl mx-auto">
                <Card>
                  <CardHeader>
                    <CardTitle>Invoice Information</CardTitle>
                    <CardDescription>Set invoice date, due date, and payment terms</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Invoice Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !invoiceDate && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {invoiceDate ? format(invoiceDate, "PPP") : "Pick a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={invoiceDate}
                              onSelect={(date) => date && setInvoiceDate(date)}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <Label>Payment Terms</Label>
                        <Select value={paymentTerms} onValueChange={handlePaymentTermsChange}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Due on Receipt</SelectItem>
                            <SelectItem value="7">Net 7 Days</SelectItem>
                            <SelectItem value="14">Net 14 Days</SelectItem>
                            <SelectItem value="30">Net 30 Days</SelectItem>
                            <SelectItem value="60">Net 60 Days</SelectItem>
                            <SelectItem value="90">Net 90 Days</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !dueDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dueDate ? format(dueDate, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dueDate}
                            onSelect={(date) => date && setDueDate(date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Reference / PO Number (Optional)</Label>
                      <Input
                        placeholder="e.g., PO-12345"
                        value={invoiceReference}
                        onChange={(e) => setInvoiceReference(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Invoice Notes (Optional)</Label>
                      <Textarea
                        placeholder="Add any additional notes or payment instructions..."
                        value={invoiceNotes}
                        onChange={(e) => setInvoiceNotes(e.target.value)}
                        rows={4}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {currentStep === 3 && (
              <div className="space-y-6 max-w-5xl mx-auto">
                <Card>
                  <CardHeader>
                    <CardTitle>Invoice Summary</CardTitle>
                    <CardDescription>Review all details before generating the invoice</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Invoice Details */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="text-sm text-muted-foreground">Project</div>
                        <div className="font-medium">{projectName}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Invoice Date</div>
                        <div className="font-medium">{format(invoiceDate, "PPP")}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Due Date</div>
                        <div className="font-medium">{format(dueDate, "PPP")}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Payment Terms</div>
                        <div className="font-medium">
                          {paymentTerms === "0"
                            ? "Due on Receipt"
                            : paymentTerms === "custom"
                            ? "Custom"
                            : `Net ${paymentTerms} Days`}
                        </div>
                      </div>
                      {invoiceReference && (
                        <div className="col-span-2">
                          <div className="text-sm text-muted-foreground">Reference</div>
                          <div className="font-medium">{invoiceReference}</div>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Line Items Summary */}
                    <div>
                      <h4 className="font-semibold mb-3">Line Items ({invoiceLineItems.length})</h4>
                      <div className="border rounded-lg max-h-[400px] overflow-auto">
                        <Table>
                          <TableHeader className="sticky top-0 bg-background">
                            <TableRow>
                              <TableHead>Description</TableHead>
                              <TableHead className="text-right">Qty</TableHead>
                              <TableHead className="text-right">Rate</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {invoiceLineItems.map((line) => (
                              <TableRow key={line.id}>
                                <TableCell className="text-sm">{line.description}</TableCell>
                                <TableCell className="text-right text-sm">
                                  {line.quantity.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right text-sm">{formatCurrency(line.rate)}</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(line.amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    <Separator />

                    {/* Totals */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                      </div>
                      {totals.tax > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Tax</span>
                          <span className="font-medium">{formatCurrency(totals.tax)}</span>
                        </div>
                      )}
                      <Separator />
                      <div className="flex items-center justify-between text-lg font-bold">
                        <span>Total</span>
                        <span>{formatCurrency(totals.total)}</span>
                      </div>
                    </div>

                    {invoiceNotes && (
                      <>
                        <Separator />
                        <div>
                          <h4 className="font-semibold mb-2">Notes</h4>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {invoiceNotes}
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-900">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  <p>
                    Ready to generate! This will create a draft invoice that you can review, edit, or send
                    to the client.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      

      {/* Footer Bar - Sticky */}
      <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSaveAsDraft}
                disabled={invoiceLineItems.length === 0}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <Save className="h-4 w-4 mr-2" />
                Save as Draft
              </Button>
            </div>
            <div className="flex gap-2">
              {currentStep > 1 && (
                <Button variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
              {currentStep < 3 ? (
                <Button onClick={handleNext} disabled={invoiceLineItems.length === 0}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleGenerateInvoice} disabled={invoiceLineItems.length === 0}>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Invoice
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Column Configurator Drawer */}
    <ColumnConfiguratorDrawer
      open={columnConfigOpen}
      onOpenChange={setColumnConfigOpen}
      columns={columns}
      onSave={setColumns}
    />
    </>
  );
}
